import { Component, Input, OnInit } from '@angular/core';

import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { validateEmail, validateEmails } from '../../../Misc/helpers/formatting/email.helper';
import { SharedReportModel } from '../../models/shared-report.model';
import { SharedReportService } from '../../services/shared-report.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { InputValidationModel } from '../../../../Common/Forms/models/input-validation.model';
import { ButtonTypesEnum }  from '../../../../Common/Forms/enums/button-types.enum';

@Component({
    selector: 'agpr-shared-report-invite-dialog',
    templateUrl: './shared-report-invite-dialog.component.html',
    styleUrls: ['./shared-report-invite-dialog.component.scss'],
    standalone: false
})
export class SharedReportInviteDialogComponent extends AbstractDialogComponent implements OnInit {
    @Input() sharedReport: SharedReportModel;
    @Input() canSkip: boolean = false;

    // Recipient chips component variables.
    splitOnPattern: RegExp = new RegExp('[,;]');

    recipientsEmpty: boolean = false;
    invalidReplyTo: boolean = false;

    shareLoading: boolean = false;

    maxInputLength: number = 255;
    inputLengthExceeding: boolean = false;

    readonly buttonType = ButtonTypesEnum;
    readonly validateEmails: InputValidationModel[] = [
        new InputValidationModel({
            errorKey: 'invalidEmailFormat',
            isValid: validateEmails
        })
    ];

    constructor(
        public coreDialogService: CoreDialogService,
        private sharedReportsService: SharedReportService,
        private userStateService: CurrentUserStateService,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'SharedReportInvite');
    }

    ngOnInit(): void {
        if (!this.sharedReport.fromName) {
            this.userStateService.getUser().pipe(take(1)).subscribe(
                user => this.sharedReport.fromName = user.getDisplayName()
            );
        }
        if (!this.sharedReport.replyTo) {
            this.userStateService.getUser().pipe(take(1)).subscribe(
                user => this.sharedReport.replyTo = user.email
            );
        }
    }

    checkRecipients() {
        return this.recipientsEmpty = this.sharedReport.recipientList.length === 0 && this.sharedReport.mailingListIds.length === 0;
    }

    validateReplyTo() {
        this.invalidReplyTo = this.sharedReport.replyTo && !validateEmail(this.sharedReport.replyTo);
    }

    async shareReport(): Promise<void> {
        this.checkRecipients();
        try {
            if (!this.recipientsEmpty && !this.invalidReplyTo) {
                this.shareLoading = true;
                // send invite emails
                await lastValueFrom(this.sharedReportsService.sendInvites(this.sharedReport));
                this.closeDialog();
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.success.heading'),
                    detail: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.success.message', {
                        name: this.sharedReport.name
                    })
                });
            }
        } catch (err) {
            this.shareLoading = false;
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.error.heading'),
                detail: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.error.message', {
                    name: this.sharedReport.name
                })
            });
        }
    }

    checkInputLength(event){
        if (event) {
            this.inputLengthExceeding = event.length === this.maxInputLength;
        }
    }

    cancel(): void {
        this.closeDialog();
    }
}
